import React, {useContext, useMemo, useRef} from "react";
import {Chart as ChartJS, ArcElement, Tooltip, Legend} from "chart.js";
import {useInView} from "motion/react";

import {Doughnut, Pie} from "react-chartjs-2";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import BlobShape from "@/components/Global/Charts/Shared/BlobShape/BlobShape";
import SvgColors from "@/components/Global/Charts/Shared/SvgColors/SvgColors";
import GlowShape from "@/components/Global/Charts/Shared/GlowShape/GlowShape";

import styles from "./Doughnut.module.scss";
import {ThemeContext} from "@/contexts/ThemeContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const initialCustomStyle = {
  borderRadius: 20,
  backgroundChartBorderRadius: 0,
  backgroundChartColor: "lightgrey",
  colorsRequired: ["green"],
  backgroundColor: "--chart-bg",
  hasBlob: true,
  hasGlow: true,
  circumference: 360,
  rotation: 200,
  animate: true,
  borderWidth: 0,
  borderColor: "--chart-bg",
  showBackground: true,
  cutout: "85%",
  rootMargin: "0px 0px -50px 0px",
};

export default function DoughnutChart(props) {
  const {children, className = "", customStyle, score, maxScore = 100, ...rest} = props;
  const doughnutStyle = {...initialCustomStyle, ...customStyle};
  const chartBgColor = getComputedStyle(document.documentElement)
    .getPropertyValue(doughnutStyle.backgroundColor)
    .trim();
  const borderColor = getComputedStyle(document.documentElement).getPropertyValue(doughnutStyle.borderColor).trim();

  const ref = useRef();
  const inView = useInView(ref, {
    margin: doughnutStyle.rootMargin,
    once: true,
  });

  const backgroundColorList = useMemo(
    () => [
      getComputedStyle(document.documentElement)
        .getPropertyValue(
          doughnutStyle.colorsRequired[0]?.startsWith("--bs-")
            ? doughnutStyle.colorsRequired[0]
            : `--bs-${doughnutStyle.colorsRequired[0]}`,
        )
        .trim(),
      "transparent",
    ],
    [doughnutStyle.colorsRequired],
  );

  const data = useMemo(
    () => ({
      datasets: [
        {
          data: [score, 100 - score],
          backgroundColor: backgroundColorList,
        },
      ],
    }),
    [score, backgroundColorList],
  );

  const dataBackground = useMemo(
    () => ({
      datasets: [
        {
          data: maxScore != 100 ? [maxScore, 100 - maxScore] : [100],
          backgroundColor:
            maxScore != 100 ? [doughnutStyle.backgroundChartColor, "transparent"] : doughnutStyle.backgroundChartColor,
        },
      ],
    }),
    [score, backgroundColorList],
  );

  const options = useMemo(
    () => ({
      circumference: doughnutStyle.circumference,
      rotation: doughnutStyle.rotation,
      animation: {
        animateRotate: doughnutStyle.animate,
        animateScale: false,
      },
      elements: {
        arc: {
          hoverOffset: 0,
          hoverBorderWidth: 0,
          hoverBackgroundColor: function (ctx) {
            return ctx.element.options.backgroundColor;
          },
          borderRadius: chart => {
            if (score == 100) return 0;
            if (doughnutStyle.circumference <= 360)
              return {outerEnd: doughnutStyle.borderRadius, innerEnd: doughnutStyle.borderRadius};

            return doughnutStyle.borderRadius;
          },
        },
      },
      responsive: true,
      cutout: doughnutStyle.cutout,
      borderColor: doughnutStyle.borderColor,
      borderWidth: doughnutStyle.borderWidth,
      hoverOffset: 0,
      plugins: {
        legend: false,
        tooltip: false,
        title: false,
      },
    }),
    [doughnutStyle],
  );

  const optionsBackground = useMemo(
    () => ({
      circumference: doughnutStyle.circumference,
      rotation: doughnutStyle.rotation,
      animation: false,
      elements: {
        arc: {
          hoverOffset: 0,
          hoverBorderWidth: 0,
          hoverBackgroundColor: function (ctx) {
            return ctx.element.options.backgroundColor;
          },
          borderRadius: score == 100 ? 0 : doughnutStyle.backgroundChartBorderRadius,
        },
      },
      responsive: true,
      cutout: doughnutStyle.cutout,
      borderColor: doughnutStyle.borderColor,
      borderWidth: doughnutStyle.borderWidth,
      hoverOffset: 0,
      plugins: {
        legend: false,
        tooltip: false,
        title: false,
      },
    }),
    [doughnutStyle],
  );

  const plugins = [];

  const {variant} = useContext(ThemeContext);
  const pieBgColor = useMemo(
    () => getComputedStyle(document.documentElement).getPropertyValue("--chart-bg").trim() || "white",
    [variant],
  );

  const pieData = useMemo(
    () => ({
      datasets: [
        {
          data: [100],
          backgroundColor: pieBgColor,
        },
      ],
    }),
    [],
  );

  const pieOptions = useMemo(
    () => ({
      animation: false,
      animations: {
        colors: false,
        x: false,
      },
      transitions: {
        active: {
          animation: {
            duration: 0,
          },
        },
      },
      hoverOffset: 0,
      hoverBackgroundColor: chartBgColor,
      hoverBorderWidth: 0,
      borderWidth: 0,
      borderColor: chartBgColor,
      plugins: {
        legend: false,
        tooltip: false,
        title: false,
      },
    }),
    [chartBgColor],
  );

  return (
    <>
      <div className={`${styles.wrapper}`} {...rest} ref={ref}>
        {inView ? (
          <div
            className={`${styles.chartContainer} ${
              doughnutStyle.circumference != 360 ? styles.fixAlign : ""
            } ${className}`}>
            <RenderIf isTrue={doughnutStyle.showBackground}>
              <Pie data={pieData} options={pieOptions} data-background="true" id="chart-background" />
            </RenderIf>

            <Doughnut
              data={dataBackground}
              options={optionsBackground}
              plugins={plugins}
              id="chart-data-background"
              style={{margin: doughnutStyle.showBackground ? "15px 15px 15px 15px" : "10px"}}
            />
            <Doughnut
              data={data}
              options={options}
              plugins={plugins}
              id="chart-data"
              style={{margin: doughnutStyle.showBackground ? "15px 15px 15px 15px" : "10px"}}
            />
            <svg width="70%" height="100%" preserveAspectRatio="xMinYMin meet" className={`${styles.legendContainer}`}>
              <foreignObject className="node" width="100%" height="100%">
                <div className={`text-center ${styles.legendContent} ${className}`}>{children}</div>
              </foreignObject>
            </svg>
          </div>
        ) : (
          <div className="w-100 h-100" style={{minHeight: "140px"}}></div>
        )}

        <RenderIf isTrue={doughnutStyle.hasGlow || doughnutStyle.hasBlob}>
          <SvgColors colors={doughnutStyle.colorsRequired} />
          {doughnutStyle.hasBlob && <BlobShape colors={doughnutStyle.colorsRequired} />}
          {doughnutStyle.hasGlow && <GlowShape colors={doughnutStyle.colorsRequired} />}
        </RenderIf>
      </div>
    </>
  );
}
